
import React from 'react';
import { format } from 'date-fns';
import { Award, BookOpen, FileCheck, FileCog, FileX } from 'lucide-react';
import { ExamResult } from '@/utils/exam/types';
import ExamActions from './ExamActions';
import ExamScore from './ExamScore';
import { Badge } from '@/components/ui/badge';

interface ExamTableRowProps {
  exam: ExamResult;
  onDelete: (examId: string) => void;
  studentId: string | number;
  onPrintCertificate: (examId: string) => void;
}

const ExamTableRow: React.FC<ExamTableRowProps> = ({ exam, onDelete, studentId, onPrintCertificate }) => {
  const examDate = exam.examDate
    ? format(new Date(exam.examDate), 'MMM dd, yyyy')
    : 'N/A';
    
  const examTypeLabels: Record<string, string> = {
    'monthly': 'Monthly',
    'annual': 'Annual',
    'test': 'Test',
    'quiz': 'Quiz',
    'other': 'Other'
  };
  
  const examTypeLabel = examTypeLabels[exam.examType] || exam.examType;
  
  // Get icon based on raw score ratio
  const getScoreIcon = () => {
    if (!exam.maxScore) return <FileX className="h-4 w-4 text-red-500" />;
    
    const score = Number(exam.obtainedScore || 0);
    const maxScore = Number(exam.maxScore || 1);
    const ratio = score / maxScore;
    
    if (ratio >= 0.7) return <FileCheck className="h-4 w-4 text-green-500" />;
    if (ratio >= 0.4) return <FileCog className="h-4 w-4 text-yellow-500" />;
    return <FileX className="h-4 w-4 text-red-500" />;
  };

  // Get badge style based on exam type
  const getExamTypeBadgeStyle = () => {
    switch (exam.examType) {
      case 'monthly':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
      case 'annual':
        return 'bg-purple-100 text-purple-800 hover:bg-purple-200';
      case 'test':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'quiz':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };
  
  return (
    <tr className="border-b hover:bg-muted/50">
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          <span className="font-medium">{exam.subject}</span>
        </div>
        {exam.programTitle && (
          <div className="text-xs text-muted-foreground mt-1">{exam.programTitle}</div>
        )}
      </td>
      <td className="py-3 px-4">
        <Badge variant="secondary" className={`${getExamTypeBadgeStyle()} font-normal`}>
          {examTypeLabel}
        </Badge>
      </td>
      <td className="py-3 px-4">{examDate}</td>
      <td className="py-3 px-4">
        <ExamScore 
          obtainedScore={exam.obtainedScore} 
          maxScore={exam.maxScore} 
          showPercentage={true} 
        />
      </td>
      <td className="py-3 px-4">
        {exam.programTitle ? (
          <div className="flex items-center gap-1">
            <Award className="h-4 w-4 text-amber-500" /> 
            <span>{exam.programTitle}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">N/A</span>
        )}
      </td>
      <td className="py-3 px-4 text-right">
        <ExamActions 
          examId={exam.id} 
          studentId={studentId} 
          onDelete={() => onDelete(exam.id)}
          onPrintCertificate={() => onPrintCertificate(exam.id)}
        />
      </td>
    </tr>
  );
};

export default ExamTableRow;
